import { SettingIcon } from '@/components/SettingIcon';
import { useT } from '@/i18n';
import type { RoundFiring, TableOrderingMode } from '@restaurant/api-client';
import {
  Block,
  BlockFooter,
  BlockTitle,
  List,
  ListItem,
  Navbar,
  Page,
  Preloader,
  f7,
} from 'framework7-react';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import { useTableOrderingConfig, useUpdateTableOrderingConfig } from '../hooks/useTableOrdering';
import { useSettingsDetailBackLink } from '@/utils/useMediaQuery';

/**
 * Table-ordering settings - mirrors the order-setup pages (same Page/Navbar/
 * List chrome, same toast + actions idioms). Speaks the operator's language:
 * service charge and tips are shown as percentages, never raw basis points.
 */
export default function TableOrderingSettingsPage() {
  const tAll = useT();
  const t = tAll.tableOrdering.settings;
  const cancel = tAll.cancel;
  const settingsBackLink = useSettingsDetailBackLink();
  const { data: config, isLoading } = useTableOrderingConfig();
  const update = useUpdateTableOrderingConfig();

  const MODE_LABEL: Record<TableOrderingMode, string> = {
    GUEST_SELF: t.guestOrderThemselves,
    STAFF_ONLY: t.staffTakeOrders,
    HYBRID: t.guestOrderStaffConfirm,
  };
  const FIRING_LABEL: Record<RoundFiring, string> = {
    AUTO_FIRE: t.sendKitchenImmediately,
    STAFF_CONFIRM: t.holdStaffAccept,
  };

  const toast = (text: string) => f7.toast.show({ text, closeTimeout: 2000, position: 'bottom' });

  if (isLoading || !config) {
    return (
      <Page name="table-ordering-settings">
        <Navbar title={t.title} backLink={settingsBackLink} />
        <Block style={{ textAlign: 'center', paddingTop: 48 }}>
          <Preloader />
        </Block>
      </Page>
    );
  }

  const save = (body: Parameters<typeof update.mutate>[0], done: string) =>
    update.mutate(body, { onSuccess: () => toast(done) });

  const pct = (bps: number) => `${(bps / 100).toFixed(bps % 100 === 0 ? 0 : 2)}%`;

  function editMode() {
    f7.actions
      .create({
        buttons: [
          [
            { label: true, text: t.whoPlacesOrders },
            ...(['GUEST_SELF', 'HYBRID', 'STAFF_ONLY'] as TableOrderingMode[]).map((m) => ({
              text: MODE_LABEL[m],
              bold: m === config?.orderingMode,
              onClick: () => save({ orderingMode: m }, t.orderingModeUpdated),
            })),
          ],
          [{ text: cancel, color: 'red' }],
        ],
      })
      .open();
  }

  function editFiring() {
    f7.actions
      .create({
        buttons: [
          [
            { label: true, text: t.whenRoundPlaced },
            ...(['AUTO_FIRE', 'STAFF_CONFIRM'] as RoundFiring[]).map((r) => ({
              text: FIRING_LABEL[r],
              bold: r === config?.roundFiring,
              onClick: () => save({ roundFiring: r }, t.roundHandlingUpdated),
            })),
          ],
          [{ text: cancel, color: 'red' }],
        ],
      })
      .open();
  }

  function editServiceCharge() {
    f7.dialog.prompt(
      t.serviceChargePrompt,
      t.serviceCharge,
      (val) => {
        const n = Number.parseFloat(val.replace(',', '.'));
        if (!Number.isFinite(n) || n < 0 || n > 100) return toast(t.enter0100);
        save({ serviceChargeBps: Math.round(n * 100) }, t.serviceChargeUpdated);
      },
      undefined,
      String(config?.serviceChargeBps ? config.serviceChargeBps / 100 : ''),
    );
  }

  function editTips() {
    f7.dialog.prompt(
      t.tipPresetsPrompt,
      t.tipSuggestions,
      (val) => {
        const parts = val
          .split(',')
          .map((s) => Number.parseFloat(s.trim().replace(',', '.')))
          .filter((n) => Number.isFinite(n) && n >= 0 && n <= 100);
        save(
          { tipPresetsBps: parts.slice(0, 6).map((n) => Math.round(n * 100)) },
          t.tipPresetsUpdated,
        );
      },
      undefined,
      (config?.tipPresetsBps ?? []).map((b) => b / 100).join(', '),
    );
  }

  return (
    <Page name="table-ordering-settings">
      <Navbar title={t.title} backLink={settingsBackLink} />

      <BlockTitle>{t.dineInOrdering}</BlockTitle>
      <BlockFooter style={{ marginTop: -6 }}>{t.friendlyMessage}</BlockFooter>
      <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
        <ListItem title={t.enabled}>
          <ToggleSwitch
            slot="after"
            checked={config.enabled}
            onChange={(v) => save({ enabled: v }, v ? t.tableOrderingOn : t.tableOrderingOff)}
          />
        </ListItem>
      </List>

      <BlockTitle>{t.howItWorks}</BlockTitle>
      <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
        <ListItem
          link="#"
          noChevron
          title={t.orderingMode}
          after={
            config.orderingMode === 'GUEST_SELF'
              ? t.modeGuest
              : config.orderingMode === 'STAFF_ONLY'
                ? t.modeStaff
                : t.modeHybrid
          }
          footer={MODE_LABEL[config.orderingMode]}
          onClick={editMode}
        >
          <div slot="media">
            <SettingIcon icon="person_2" color="#6366f1" />
          </div>
        </ListItem>
        <ListItem
          link="#"
          noChevron
          title={t.roundHandling}
          after={config.roundFiring === 'AUTO_FIRE' ? t.firingAuto : t.firingConfirm}
          footer={FIRING_LABEL[config.roundFiring]}
          onClick={editFiring}
        >
          <div slot="media">
            <SettingIcon icon="flame" color="#ff9500" />
          </div>
        </ListItem>
        <ListItem title={t.autoPrintKitchen}>
          <div slot="media">
            <SettingIcon icon="printer" color="#34c759" />
          </div>
          <ToggleSwitch
            slot="after"
            checked={config.kitchenAutoPrint}
            onChange={(v) => save({ kitchenAutoPrint: v }, t.kitchenTicketUpdated)}
          />
        </ListItem>
      </List>

      <BlockTitle>{t.charges}</BlockTitle>
      <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
        <ListItem
          link="#"
          noChevron
          title={t.serviceCharge}
          after={config.serviceChargeBps > 0 ? pct(config.serviceChargeBps) : t.chargeOff}
          onClick={editServiceCharge}
        >
          <div slot="media">
            <SettingIcon icon="percent" color="#14b8a6" />
          </div>
        </ListItem>
        <ListItem
          link="#"
          noChevron
          title={t.tipSuggestions}
          after={
            config.tipPresetsBps.length > 0
              ? config.tipPresetsBps.map((b) => pct(b)).join(' · ')
              : t.tipsNone
          }
          onClick={editTips}
        >
          <div slot="media">
            <SettingIcon icon="heart" color="#ff2d55" />
          </div>
        </ListItem>
      </List>

      <BlockTitle>{t.tableQRCodes}</BlockTitle>
      <BlockFooter style={{ marginTop: -6 }}>{t.printQRDesc}</BlockFooter>
      <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
        <ListItem title={t.printableQR} link="/settings/table-ordering/qr">
          <div slot="media">
            <SettingIcon icon="qrcode" color="#3b82f6" />
          </div>
        </ListItem>
      </List>
    </Page>
  );
}
